/**
 * ⓘ 툴팁 , 동그라미 안 i. **이 화면의 유일한 물음표 아이콘이다.** (그리드펄스 DS 에서 가져옴)
 *
 * 긴 설명을 판에 욱여넣지 않고 표시 뒤로 접는다. 판에는 값과 한 줄 결론만 남고,
 * 산정 근거,자료 출처,주의사항은 이 아이콘에 마우스를 얹거나 눌러야 나온다.
 * 지금 행동해야 하는 경고(필수값 누락,조건 미충족)는 접지 않는다 , 그건 설명이 아니라 상태다.
 *
 * 풍선은 body 에 portal 로 띄운다(스크롤 판에 잘리지 않게). 자리는 스스로 잰다.
 * 모양은 전부 globals.css 의 .tip 계열이 정한다 , 여기에는 색도 크기도 없다.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { OVERLAY_LAYOUT } from "@/lib/design";

interface TipProps {
  label: string;
  tip: ReactNode;
  children: ReactNode;
  triggerClassName?: string;
  className?: string;
}

export function Tip({ label, tip, children, triggerClassName, className }: TipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btn.current;
      const el = bubble.current;
      if (!b || !el) return;
      const r = b.getBoundingClientRect();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const below = r.bottom + OVERLAY_LAYOUT.tooltipGap;
      const top = below + h > window.innerHeight - OVERLAY_LAYOUT.tooltipEdge
        ? Math.max(OVERLAY_LAYOUT.tooltipEdge, r.top - OVERLAY_LAYOUT.tooltipGap - h)
        : below;
      const left = Math.min(
        Math.max(OVERLAY_LAYOUT.tooltipEdge, r.left + r.width / 2 - w / 2),
        Math.max(OVERLAY_LAYOUT.tooltipEdge, window.innerWidth - w - OVERLAY_LAYOUT.tooltipEdge),
      );
      setPos({ top, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <span
      className={["tip", className ?? ""].join(" ").trim()}
      onPointerEnter={(e) => {
        if (e.pointerType !== "touch") setOpen(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== "touch") close();
      }}
    >
      <button
        ref={btn}
        type="button"
        className={triggerClassName ?? "tip-btn"}
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        title={label}
        onClick={() => (open ? close() : setOpen(true))}
        onFocus={() => setOpen(true)}
        onBlur={close}
      >
        {children}
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <span ref={bubble} role="tooltip" id={id} className="tip-bubble" style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden" }}>
            {tip}
          </span>,
          document.body,
        )}
    </span>
  );
}

/** ⓘ , 이 화면의 유일한 물음표 아이콘 */
export function InfoTip({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <Tip label={label} tip={children} className={className}>
      <svg viewBox="0 0 20 20" className="size-icon" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
        <circle cx="10" cy="10" r="8" />
        <path d="M10 9v5" strokeLinecap="round" />
        <circle cx="10" cy="6.2" r="0.9" fill="currentColor" stroke="none" />
      </svg>
    </Tip>
  );
}
